import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import Papa from 'papaparse';
import { useCallback } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import type { ChartDownloadFormat } from '@/pages/analytics/components/ChartDownloadMenu';
import {
  downloadElementPdf,
  type DownloadElementPdfOptions,
} from '@/pages/analytics/utils/downloadElementPdf';
import type { NormalizedExport } from '@/pages/analytics/utils/normalizeChartRows';

// File-naming convention: `<stem>_<YYYY-MM-DD>_<HHMM>.<ext>`. The org id and
// the filter date range are intentionally NOT in the filename — they bloat
// the name with operator-internal identifiers. The download timestamp is in
// local time so it matches the user's clock.
const buildFilename = (stem: string, ext: ChartDownloadFormat): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `${stem}_${date}_${time}.${ext}`;
};

const downloadCsv = (data: NormalizedExport, filename: string) => {
  const csv = Papa.unparse({
    fields: data.columns,
    data: data.rows.map(row => data.columns.map(col => row[col] ?? '')),
  });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, filename);
};

const downloadXlsx = (data: NormalizedExport, filename: string) => {
  const ws = XLSX.utils.json_to_sheet(data.rows, { header: data.columns });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, filename);
};

const downloadPdf = (data: NormalizedExport, filename: string, title: string) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const margin = 36;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usable = pageWidth - margin * 2;
  const gap = 8; // inter-column padding
  const lineHeight = 14;

  doc.setFontSize(14);
  doc.text(title, margin, margin);
  doc.setFontSize(9);

  // Clip a string to a column width, appending an ellipsis — so a long value
  // (e.g. an agent email) never bleeds into the next column. Binary-searches the
  // longest prefix that fits at the current font.
  const fit = (text: string, maxWidth: number): string => {
    if (maxWidth <= 0) return '';
    if (doc.getTextWidth(text) <= maxWidth) return text;
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (doc.getTextWidth(`${text.slice(0, mid)}…`) <= maxWidth) lo = mid;
      else hi = mid - 1;
    }
    return `${text.slice(0, lo)}…`;
  };

  // Auto-size each column to its content (header + a sample of rows), then scale
  // to fit the page. Equal-width columns overflowed for long IDs/emails.
  const sample = data.rows.slice(0, 200);
  doc.setFont('helvetica', 'bold');
  const rawWidths = data.columns.map(col => doc.getTextWidth(String(col)));
  doc.setFont('helvetica', 'normal');
  data.columns.forEach((col, i) => {
    for (const row of sample) {
      const v = row[col];
      if (v != null) rawWidths[i] = Math.max(rawWidths[i], doc.getTextWidth(String(v)));
    }
    rawWidths[i] += gap;
  });
  const totalRaw = rawWidths.reduce((a, b) => a + b, 0);
  const scale = totalRaw > usable ? usable / totalRaw : 1;
  const colWidths = rawWidths.map(w => w * scale);
  const colX: number[] = [];
  let x = margin;
  for (const w of colWidths) {
    colX.push(x);
    x += w;
  }

  const startY = margin + 18;
  doc.setFont('helvetica', 'bold');
  data.columns.forEach((col, i) => doc.text(fit(String(col), colWidths[i] - gap), colX[i], startY));
  doc.setFont('helvetica', 'normal');

  let y = startY + lineHeight;
  for (const row of data.rows) {
    if (y > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
    data.columns.forEach((col, i) => {
      const v = row[col];
      doc.text(v != null ? fit(String(v), colWidths[i] - gap) : '', colX[i], y);
    });
    y += lineHeight;
  }

  doc.save(filename);
};

export const useDownloadChart = () => {
  return useCallback(
    async (
      data: NormalizedExport,
      format: ChartDownloadFormat,
      title?: string,
      element?: HTMLElement | null,
      pdfOptions?: DownloadElementPdfOptions,
    ) => {
      try {
        const filename = buildFilename(data.filenameStem, format);
        if (format === 'csv') downloadCsv(data, filename);
        else if (format === 'xlsx') downloadXlsx(data, filename);
        else if (element) await downloadElementPdf(element, filename, pdfOptions);
        else downloadPdf(data, filename, title || data.filenameStem.replace(/_/g, ' '));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to download';
        toast.error(message);
      }
    },
    [],
  );
};
